import React, { useMemo } from 'react';
import { Div, Button, Icon, Text } from '@pinpoint/design-kit';
import { sum } from 'src/functions';

import Cell from 'src/infographics/roi_calculator_v2/cell';

/* eslint-disable react/prop-types,react/no-array-index-key */

function OutputGrid({
	output: {
		y1: {
			baselineLessRiskCost,
			baselinePremiums,
			baselinePremiumsSum,
			baselineRiskCosts,
			baselineRiskCostsSum,
			oneYearBaselinePifs,
			oneYearBaselinePifsSum,
			pinpointLessRiskCost,
			pinpointPifs,
			pinpointPifsSum,
			pinpointPremiums,
			pinpointPremiumsSum,
			pinpointRiskCosts,
			pinpointRiskCostsSum,
		},
	},
	acquisitionDistribution,
	baselineLift,
	handleCalculate,
	resetAcquisitionDistribution,
	inputRefs,
	modelType,
	onChange,
	pinpointLift,
}) {
	const acquisitionDistributionSum = useMemo(() => sum(acquisitionDistribution), [acquisitionDistribution]);
	const acquisitionDistributionIsDefault = useMemo(
		() => [30, 25, 20, 15, 10, 0, 0, 0, 0, 0].every((elem, idx) => elem === acquisitionDistribution[idx]),
		[acquisitionDistribution]
	);
	const goodAcquisitionDistribution = useMemo(() => acquisitionDistributionSum === 100, [acquisitionDistributionSum]);
	const showBaseline = modelType.includes('ACT_');
	const columnCount = showBaseline ? 10 : 9; // NOTE: number of columns
	const buttonOffset = columnCount - 4; // NOTE: offset needed for the recalc buttons
	const gridTemplateColumns = showBaseline
		? '.35fr repeat(5, .65fr) 1fr repeat(3, .85fr)'
		: '.35fr repeat(4, .65fr) 1fr repeat(3, .85fr)';
	return (
		<Div d="grid" w="100%" style={{ gridTemplateColumns, gap: '1px' }}>
			<Cell h="1.25rem" backgroundColor="brand200" />
			{showBaseline ? <Cell h="1.25rem" backgroundColor="skyBlue" /> : null}
			<Cell h="1.25rem" backgroundColor="skyBlue" />
			<Cell h="1.25rem" backgroundColor="skyBlue" />
			<Cell h="1.25rem" backgroundColor="skyBlue" />
			<Cell h="1.25rem" backgroundColor="brand200" />
			<Cell h="1.25rem" backgroundColor="tealLight" />
			<Cell h="1.25rem" backgroundColor="tealLight" />
			<Cell h="1.25rem" backgroundColor="tealLight" />
			<Cell h="1.25rem" backgroundColor="tealLight" />
			<Cell
				backgroundColor="marigoldPale"
				pad="header"
				inputValue="Decile"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			{showBaseline ? (
				<Cell
					backgroundColor="skyBluePale"
					pad="header"
					inputValue="Baseline Lift"
					textWeight="700"
					textColor="black"
					textAlign="right"
					align="start"
				/>
			) : null}
			<Cell
				backgroundColor="skyBluePale"
				pad="header"
				inputValue="Baseline PIF after Year 1"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="skyBluePale"
				pad="header"
				inputValue="Baseline Risk Cost"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="skyBluePale"
				pad="header"
				inputValue="Baseline Premiums"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="marigoldPale"
				pad="header"
				inputValue="Pinpoint Lift"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="seaGreenPale"
				pad="header"
				inputValue="Pinpoint New Business Distribution"
				TextPropsProp={{
					'data-test': 'acq-dist-header',
				}}
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="seaGreenPale"
				pad="header"
				inputValue="Pinpoint PIF after Year 1"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="seaGreenPale"
				pad="header"
				inputValue="Pinpoint Risk Cost"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			<Cell
				backgroundColor="seaGreenPale"
				pad="header"
				inputValue="Pinpoint Premiums"
				textWeight="700"
				textColor="black"
				textAlign="right"
				align="start"
			/>
			{Array(10)
				.fill(null)
				.map((_, i) => (
					<>
						<Cell
							key={`row-${i}-decile`}
							backgroundColor="marigoldPale"
							inputValue={i + 1}
							textColor="shade4D"
							textAlign="right"
							format="numeric"
						/>
						{showBaseline ? (
							<Cell
								key={`row-${i}-baseline-lift`}
								backgroundColor="skyBluePale"
								inputValue={baselineLift[i]}
								formattingOptions={{ decimalScale: 2 }}
								textColor="shade4D"
								textAlign="right"
								format="numeric"
							/>
						) : null}
						<Cell
							key={`row-${i}-baseline-pifs`}
							backgroundColor="skyBluePale"
							inputValue={oneYearBaselinePifs[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="numeric"
						/>
						<Cell
							key={`row-${i}-baseline-risk-costs`}
							backgroundColor="skyBluePale"
							inputValue={baselineRiskCosts[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="currency"
						/>
						<Cell
							key={`row-${i}-baseline-premiums`}
							backgroundColor="skyBluePale"
							inputValue={baselinePremiums[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="currency"
						/>
						<Cell
							key={`row-${i}-pinpoint-lift`}
							backgroundColor="marigoldPale"
							inputValue={pinpointLift[i]}
							textColor="shade4D"
							textAlign="right"
							format="numeric"
							formattingOptions={{
								decimalScale: 2,
								allowNegative: false,
							}}
						/>
						<Cell
							key={`row-${i}-acq-distributions`}
							backgroundColor="seaGreenPale"
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="percentage"
							inputPath={`acquisitionDistribution.${i}`}
							inputValue={acquisitionDistribution[i]}
							inputRefs={inputRefs}
							idx={i}
							onChange={onChange}
							type="input"
						/>
						<Cell
							key={`row-${i}-post-interventions`}
							backgroundColor="seaGreenPale"
							inputValue={pinpointPifs[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="numeric"
						/>
						<Cell
							key={`row-${i}-new-risk-cost`}
							backgroundColor="seaGreenPale"
							inputValue={pinpointRiskCosts[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="currency"
						/>
						<Cell
							key={`row-${i}-premiums`}
							backgroundColor="seaGreenPale"
							inputValue={pinpointPremiums[i]}
							formattingOptions={{
								allowNegative: false,
							}}
							textColor="shade4D"
							textAlign="right"
							format="currency"
						/>
					</>
				))}
			<Cell backgroundColor="marigoldPale" />
			{showBaseline ? <Cell backgroundColor="skyBluePale" /> : null}
			<Cell backgroundColor="skyBluePale" />
			<Cell backgroundColor="skyBluePale" />
			<Cell backgroundColor="skyBluePale" />
			<Cell backgroundColor="marigoldPale" />
			<Cell backgroundColor="seaGreenPale" />
			<Cell backgroundColor="seaGreenPale" />
			<Cell backgroundColor="seaGreenPale" />
			<Cell backgroundColor="seaGreenPale" />
			<Cell
				backgroundColor="marigoldDarkPale"
				pad="header"
				inputValue=""
				textWeight="bold"
				textColor="shade4D"
				textAlign="right"
			/>
			{showBaseline ? (
				<Cell
					backgroundColor="powderBluePale"
					pad="header"
					inputValue=""
					textWeight="bold"
					textColor="shade4D"
					textAlign="right"
				/>
			) : null}
			<Cell
				backgroundColor="powderBluePale"
				pad="header"
				inputValue={oneYearBaselinePifsSum}
				formattingOptions={{
					allowNegative: false,
				}}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="numeric"
			/>
			<Cell
				backgroundColor="powderBluePale"
				pad="header"
				inputValue={baselineRiskCostsSum}
				formattingOptions={{
					allowNegative: false,
				}}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="currency"
			/>
			<Cell
				backgroundColor="powderBluePale"
				pad="header"
				inputValue={baselinePremiumsSum}
				formattingOptions={{
					allowNegative: false,
				}}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="currency"
			/>
			<Cell
				backgroundColor="marigoldDarkPale"
				pad="header"
				inputValue=""
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
			/>
			<Cell
				backgroundColor="powderGreenPale"
				pad="header"
				inputValue={acquisitionDistributionSum}
				textWeight="700"
				textColor={goodAcquisitionDistribution ? 'shade4D' : 'danger800'}
				textAlign="right"
				format="percentage"
				formattingOptions={{
					allowNegative: false,
				}}
			/>
			<Cell
				backgroundColor="powderGreenPale"
				pad="header"
				inputValue={pinpointPifsSum}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="numeric"
				formattingOptions={{
					allowNegative: false,
				}}
			/>
			<Cell
				backgroundColor="powderGreenPale"
				pad="header"
				inputValue={pinpointRiskCostsSum}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="currency"
				formattingOptions={{
					allowNegative: false,
				}}
			/>
			<Cell
				backgroundColor="powderGreenPale"
				pad="header"
				inputValue={pinpointPremiumsSum}
				textWeight="700"
				textColor="shade4D"
				textAlign="right"
				format="currency"
				formattingOptions={{
					allowNegative: false,
				}}
			/>
			<Cell
				pad="none"
				backgroundColor="white"
				inputValue="Net Underwriting Results"
				textWeight="700"
				textColor="shade4D"
				justify="start"
				divStyle={{ gridColumnStart: 1, gridColumnEnd: buttonOffset + 1 }}
			/>
			<Div d="flex" justify="center" align="center" p="1px" backgroundColor="white">
				<Button
					bg="brand500"
					hoverBg="brand600"
					p={{ x: '.625rem' }}
					m={{ b: '.25rem', r: '1px' }}
					h="1.125rem"
					disabled={acquisitionDistributionIsDefault}
					onClick={resetAcquisitionDistribution}
				>
					<Icon name="Reload" size="0.625rem" viewBox="0 0 12 11.75" color="white" m={{ r: '.25rem' }} />
					<Text textSize="small">DEFAULT</Text>
				</Button>
				<Div data-test="calc-btn-div">
					<Button
						bg="brand500"
						hoverBg="brand600"
						p={{ x: '.625rem' }}
						m={{ b: '.25rem', l: '1px' }}
						h="1.125rem"
						disabled={!goodAcquisitionDistribution}
						onClick={handleCalculate}
					>
						<Icon name="Roi" size="0.625rem" viewBox="0 0 16 16" color="white" m={{ r: '.25rem' }} />
						<Text textSize="small">CALCULATE</Text>
					</Button>
				</Div>
			</Div>
			<Div
				style={{ gridColumnStart: 1, gridColumnEnd: columnCount + 1 }}
				border={{ b: '1px solid' }}
				borderColor="gray500"
			/>
			<Cell
				pad="none"
				backgroundColor="white"
				inputValue="Baseline Premiums Less Risk Cost"
				textWeight="500"
				textColor="shade4D"
				justify="start"
				divStyle={{ gridColumnStart: 1, gridColumnEnd: buttonOffset - 2 }}
			/>
			<Cell
				backgroundColor="powderBluePale"
				inputValue={baselineLessRiskCost}
				textWeight="800"
				textColor="shade4D"
				textAlign="right"
				format="currency"
				formattingOptions={{
					allowNegative: false,
				}}
				divStyle={{ gridColumnStart: buttonOffset - 2, gridColumnEnd: buttonOffset }}
			/>
			<Div
				style={{ gridColumnStart: 1, gridColumnEnd: columnCount + 1 }}
				border={{ b: '1px solid' }}
				borderColor="gray500"
			/>
			<Cell
				pad="none"
				backgroundColor="white"
				inputValue="Pinpoint Premiums Less New Risk Cost"
				textWeight="500"
				textColor="shade4D"
				justify="start"
				divStyle={{ gridColumnStart: 1, gridColumnEnd: columnCount - 1 }}
			/>
			<Cell
				backgroundColor="powderBluePale"
				inputValue={pinpointLessRiskCost}
				textWeight="800"
				textColor="shade4D"
				textAlign="right"
				format="currency"
				formattingOptions={{
					allowNegative: false,
				}}
				divStyle={{ gridColumnStart: columnCount - 1, gridColumnEnd: columnCount + 1 }}
			/>
			<Div
				style={{ gridColumnStart: 1, gridColumnEnd: columnCount + 1 }}
				border={{ b: '1px solid' }}
				borderColor="gray500"
			/>
		</Div>
	);
}

export default OutputGrid;
